import { Link, Outlet, useRouter, useRouterState } from "@tanstack/react-router";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import type { Me } from "@/api/schemas";
import { loadWords, useWordsVersion, w } from "@/api/words";
import { ChevronDown, KeyRound, LogOut, Menu as MenuIcon, Shield, X } from "lucide-react";

import { TitleSlot } from "@/components/ui/page-header";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

import { uk } from "../i18n/uk";
import { surfaces } from "../surfaces/registry";
import { rememberInterrupted, rememberSessionOver } from "../surfaces/S-00/return-to";

import {
  askAttention,
  askFreshness,
  askMe,
  askMenu,
  askStatus,
  putSeen,
  type AttentionAnswer,
  type BackupAnswer,
  type FreshAnswer,
  type Menu,
  type MenuBroken,
} from "./api";
import { askDataState, type StateAnswer } from "./dataState";
import { LiveRegion } from "./announce";
import { entriesOf, MenuSearch } from "./menu-search";
import { Nav } from "./nav";
import { StatusBar } from "./StatusLine";
import { pathOf } from "./nav-paths";
import { forgetLive, markLive, wasLive, watchSessions } from "./session";

// Оболочка: шапка и боковое меню на каждой поверхности.
//
// ⚠⚠ **Шапка** — сквозные признаки всех поверхностей, и ни один из них не
// зависит от права на отдельную поверхность:
//
// - **поиск** — поле «Знайти…» справа (будущий общий поиск, пока по экранам; решение 067);
// - **колокольчик «Сповіщення»** — приглушён, пока экрана `S-19` нет (решение 067);
// - **профиль** — логин, вход в «Мій вхід» и «Вийти» (раздел `S-00`, §3.6);
// - **ссылка «до змісту»** — первая в порядке фокуса;
// - **логотип** ведёт на дашборд (`G80`, часть 1).
//
// ⚠ Индикатор свежести живёт не в шапке, а нижней строкой меню (F-013, решение
// 066) — своей ручкой, открытой каждой действующей учётной записи (решение
// оркестратора 84): переход на `S-10` — только тем, кому она выдана, остальным —
// строка о закрытом вместо мёртвого пункта (`НВ`, §2).
//
// ⚠⚠ **Меню** — из `CFG-141` по правам входа (`api/shell.py`): невыданное
// сервер уже убрал вместе с пустыми группами (`G80`, часть 5), клиент
// ничего не прячет и не достраивает. Подача меню — `./nav.tsx`: готовые пункты
// ссылками, будущие приглушённо на своих местах (решение 028).
//
// ⚠⚠ **Окончание сессии** перехватывается здесь у запросов всех поверхностей
// (`./session.ts`): адрес, где прервали, запоминается, и человек идёт на вход.

/** Что знает оболочка. `null` в полях — ответа ещё нет. */
export interface ShellState {
  readonly fresh: FreshAnswer | null;
  /** Кто вошёл. `false` — входа нет (спросили, ответа «кто» нет). */
  readonly me: Me | null | false;
  /** Меню. `false` — не пришло; `MenuBroken` — настройка меню испорчена (отказ с причиной). */
  readonly menu: Menu | MenuBroken | null | false;
  /** Дела «Потребує уваги» (F-013, решение 066). `null` — ответа ещё нет. */
  readonly attention: AttentionAnswer | null;
  /** Резервная копия строки состояния меню. `null` — ответа ещё нет. */
  readonly backup: BackupAnswer | null;
  /** Отметить дела виденными (на сервере хранится только это). */
  readonly markSeen: (keys: readonly string[]) => void;
  /** Спросить всё заново: переход, возврат во вкладку, вход, выход, правка. */
  readonly refresh: () => Promise<void>;
}

export const Held = createContext<ShellState | null>(null);

/** Оболочка из каркаса. Поверхность, отрисованная без каркаса, получает `null`. */
export function useShell(): ShellState | null {
  return useContext(Held);
}

// --- Состояние данных поверхности `S-10` ----------------------------------------
//
// ⚠ Шапка на `/api/s-10/state` не ходит (решение оркестратора 84), и
// состояние данных держит сама поверхность `S-10`. Ответ её переключателя меняет
// и шапку — поэтому `put` просит оболочку спросить свежесть заново.

/** Состояние данных поверхности `S-10`. */
export interface DataStateHeld {
  /** `null` — ответа ещё нет. */
  readonly answer: StateAnswer | null;
  readonly refresh: () => Promise<void>;
  /** Положить состояние, пришедшее ответом на действие. */
  readonly put: (answer: StateAnswer) => void;
}

/** Одно обращение за состоянием данных с разбором нечитаемого. */
async function askOnce(): Promise<StateAnswer> {
  try {
    return await askDataState();
  } catch (cause) {
    // ⚠ Нечитаемый ответ не прячется: причина уходит в консоль. Сюда же
    // попадает разошедшийся возраст данных (`FreshnessDefect`).
    console.error(cause);
    return { kind: "silent" };
  }
}

export function useDataStateHeld(): DataStateHeld {
  const shell = useShell();
  const again = shell?.refresh;
  const [answer, setAnswer] = useState<StateAnswer | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setAnswer(await askOnce());
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const put = useCallback(
    (next: StateAnswer) => {
      setAnswer(next);
      // Шапка говорит о тех же данных: переключатель поменял их — она спросит.
      if (again !== undefined) void again();
    },
    [again],
  );

  return useMemo(() => ({ answer, refresh, put }), [answer, refresh, put]);
}

// --- Оболочка -----------------------------------------------------------------------

function ShellProvider({ children }: { readonly children: ReactNode }) {
  const [fresh, setFresh] = useState<FreshAnswer | null>(null);
  const [me, setMe] = useState<Me | null | false>(null);
  const [menu, setMenu] = useState<Menu | MenuBroken | null | false>(null);
  const [attention, setAttention] = useState<AttentionAnswer | null>(null);
  const [backup, setBackup] = useState<BackupAnswer | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    const gotMe = await askMe();
    if (gotMe === null) {
      // Входа нет: ни свежести, ни меню, ни профиля — спрашивать не у кого.
      setMe(false);
      setFresh({ kind: "no-session" });
      setMenu(null);
      setAttention(null);
      setBackup(null);
      return;
    }
    markLive();
    setMe(gotMe);
    if (gotMe.must_change_password) {
      // Временный пароль: до смены сервер не открывает ни меню, ни свежести —
      // спрашивать их значит показать человеку «система не відповіла» вместо причины.
      setFresh({ kind: "no-session" });
      setMenu(null);
      setAttention(null);
      setBackup(null);
      return;
    }
    const [gotFresh, gotMenu, gotAttention, gotBackup] = await Promise.all([
      askFreshness(),
      askMenu(),
      askAttention(),
      askStatus(),
    ]);
    setFresh(gotFresh);
    setMenu(gotMenu ?? false);
    setAttention(gotAttention);
    setBackup(gotBackup);
  }, []);

  // Не записалось — молча не теряется: следующий показ снова назовёт дело новым.
  const markSeen = useCallback((keys: readonly string[]): void => {
    void putSeen(keys);
  }, []);

  const held = useMemo<ShellState>(
    () => ({ fresh, me, menu, attention, backup, markSeen, refresh }),
    [fresh, me, menu, attention, backup, markSeen, refresh],
  );
  return <Held.Provider value={held}>{children}</Held.Provider>;
}

/**
 * Когда оболочка спрашивает заново.
 *
 * ⚠⚠ Свежесть **стареет сама**: вопрос повторяется на каждом переходе между
 * поверхностями и при возврате во вкладку. Часов нет намеренно — подписка по
 * таймеру била бы по ручке каждую минуту (предел назван: пока человек сидит на
 * одном экране, подпись не обновляется сама). Первый вопрос — переходом на
 * первый же адрес.
 */
function Rewatch() {
  const shell = useShell();
  const refresh = shell?.refresh;
  const at = useRouterState({ select: (one) => one.location.pathname });

  useEffect(() => {
    if (refresh !== undefined) void refresh();
  }, [at, refresh]);

  useEffect(() => {
    if (refresh === undefined) return;
    // Возврат во вкладку даёт и `focus`, и `visibilitychange` почти разом — спрашиваем один раз.
    let last = 0;
    const again = () => {
      if (document.visibilityState !== "visible" || Date.now() - last < 1000) return;
      last = Date.now();
      void refresh();
    };
    window.addEventListener("focus", again);
    document.addEventListener("visibilitychange", again);
    return () => {
      window.removeEventListener("focus", again);
      document.removeEventListener("visibilitychange", again);
    };
  }, [refresh]);

  return null;
}

/**
 * Окончание сессии у любого запроса любой поверхности.
 *
 * Этот браузер видел живую сессию — это прерванная работа: адрес запоминается
 * (если он адрес поверхности), слова «сесія закінчилась» — тоже, и человек идёт
 * на вход; после входа он вернётся туда же, если поверхность ему по-прежнему
 * выдана (решение оркестратора 71 (б), `S-00/return-to.ts`). Живой не было —
 * это старт (вход по закладке): вход, а после него дашборд (`G93`).
 */
function SessionWatch() {
  const router = useRouter();
  useEffect(() => {
    const login = pathOf("S-00");
    return watchSessions(() => {
      const at = router.state.location.pathname;
      if (login === undefined || at === login) return;
      if (wasLive()) {
        rememberInterrupted(surfaces, at);
        rememberSessionOver(w("session_expired"));
      }
      forgetLive();
      void loadWords().finally(() => void router.navigate({ to: login }));
    });
  }, [router]);
  return null;
}

/**
 * Временный пароль (`S-46`, ответ владельца 06.10): до смены ни одна страница не
 * открыта, поэтому с любого адреса — на «Вхід», к форме смены пароля. Иначе человек
 * видел бы пустой дашборд и отказ на каждой странице, не зная причины.
 */
function MustChangeWatch() {
  const shell = useShell();
  const router = useRouter();
  const at = useRouterState({ select: (one) => one.location.pathname });
  const must = shell !== null && shell.me !== null && shell.me !== false && shell.me.must_change_password;
  useEffect(() => {
    const login = pathOf("S-00");
    if (must && login !== undefined && at !== login) void router.navigate({ to: login });
  }, [must, at, router]);
  return null;
}

/** Две буквы логина — кружок профиля. Не слово и не имя: значок учётной записи. */
function initialsOf(login: string): string {
  return login.slice(0, 2).toUpperCase();
}

/** Профиль в шапке: логин, «Мій вхід», «Вийти» (раздел `S-00`, §3.6). */
function Profile({ me }: { readonly me: Me }) {
  const router = useRouter();
  const shell = useShell();
  const login = pathOf("S-00");
  const [busy, setBusy] = useState(false);
  // Меню закрывается выбором пункта: иначе после перехода оно висело открытым.
  const [open, setOpen] = useState(false);

  async function leave(): Promise<void> {
    if (busy) return;
    setOpen(false);
    setBusy(true);
    try {
      await fetch("/api/auth/logout", { method: "POST", headers: { accept: "application/json" } });
    } catch {
      // Сервер не ответил — выход всё равно продолжается на экране входа:
      // там видно, осталась ли сессия.
    }
    forgetLive();
    await loadWords();
    setBusy(false);
    if (login !== undefined) await router.navigate({ to: login });
    if (shell !== null) void shell.refresh();
  }

  const who = (
    <>
      <span
        aria-hidden="true"
        className="inline-flex size-8 shrink-0 items-center justify-center rounded-full border border-line-strong bg-overlay text-xs font-semibold text-ink"
      >
        {initialsOf(me.login)}
      </span>
      <span className="text-sm font-medium text-ink-2 group-hover:text-ink">{me.login}</span>
    </>
  );

  const ROW =
    "flex w-full items-center gap-sm rounded-control px-sm py-xs text-left text-sm text-ink-2 " +
    "transition-colors hover:bg-raised hover:text-ink";

  // ⚠ Профиль — одна кнопка-меню: «Мій вхід» и «Вийти» внутри. Две мишени
  // рядом в углу шапки промахивались друг в друга; «Вийти» не должен стоять
  // там, куда тянется рука за профилем.
  return (
    <div className="flex items-center">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          aria-label={`${uk.nav.profile}: ${me.login}`}
          className="group relative -mr-xs flex min-h-8 items-center gap-xs rounded-control py-2xs pr-xs pl-2xs transition-colors hover:bg-overlay"
        >
          {who}
          <ChevronDown aria-hidden="true" className="size-4 text-ink-3" />
        </PopoverTrigger>
        <PopoverContent
          align="end"
          aria-label={`${uk.nav.profile}: ${me.login}`}
          className="w-56 gap-2xs border border-line p-2xs"
        >
          {/* Чей это вход — первой строкой меню, как и на кнопке. */}
          <p data-private className="truncate border-b border-line-subtle px-sm pt-2xs pb-xs text-xs text-ink-3">
            {me.login}
          </p>
          {login === undefined ? null : (
            <Link to={login} onClick={() => setOpen(false)} className={ROW}>
              <KeyRound aria-hidden="true" className="size-4 text-ink-3" />
              {uk.nav.profile}
            </Link>
          )}
          <button type="button" onClick={() => void leave()} aria-busy={busy} className={ROW}>
            <LogOut aria-hidden="true" className="size-4 text-ink-3" />
            {uk.nav.signOut}
          </button>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/** Знак системы: щит. Золото здесь — знак, а не состояние. */
function Brand({ linked }: { readonly linked: string | undefined }) {
  const face = (
    <span className="flex items-center gap-sm">
      <span
        aria-hidden="true"
        className="inline-flex size-8 items-center justify-center rounded-control border border-accent-line/40 bg-accent-wash text-accent-ink"
      >
        <Shield className="size-4" />
      </span>
      {/* Телефон: только знак — место в шапке отдано заголовку экрана; имя остаётся для чтеца. */}
      <span className="sr-only text-base font-semibold tracking-tight text-ink sm:not-sr-only">{uk.appName}</span>
    </span>
  );
  if (linked === undefined) return face;
  return (
    <Link
      to={linked}
      aria-label={`${uk.appName} · ${uk.nav.home}`}
      className="relative rounded-control after:absolute after:-inset-touch-pad after:content-['']"
    >
      {face}
    </Link>
  );
}

/**
 * Шапка — одна строка на всю ширину: знак системы над меню, заголовок экрана и
 * справа поиск, колокольчик, профиль. Прилипшая к верху. Свежесть данных — в нижней
 * строке состояния.
 *
 * ⚠ Одна левая линия (решение 067): знак системы стоит на тех же 16 px, что значки меню;
 * заголовок — в колонке полотна (то же поле и та же `max-w-page`, что у `main`), а правые
 * края шапки и строки состояния — на одном `px-md`.
 */
function Header({
  menuId,
  menuOpen,
  onMenu,
  onGo,
  onSlot,
}: {
  readonly menuId: string;
  readonly menuOpen: boolean;
  readonly onMenu: () => void;
  readonly onGo: () => void;
  readonly onSlot: (node: HTMLElement | null) => void;
}) {
  useWordsVersion();
  const shell = useShell();
  // Живая высота шапки (`--header-live`): ниже `lg` она в две строки, и липкие
  // части страницы (шапка таблицы) прилипают к ней, а не к «3,5 rem».
  const bar = useRef<HTMLElement>(null);
  useEffect(() => {
    const node = bar.current;
    if (node === null || typeof ResizeObserver === "undefined") return;
    const root = document.documentElement;
    const set = () => root.style.setProperty("--header-live", `${node.getBoundingClientRect().height}px`);
    const watch = new ResizeObserver(set);
    watch.observe(node);
    set();
    return () => {
      watch.disconnect();
      root.style.removeProperty("--header-live");
    };
  }, []);
  const me = shell?.me ?? null;
  const signedIn = me !== null && me !== false;
  const home = pathOf("S-52");
  const menu = shell?.menu;
  const shownMenu = menu !== undefined && menu !== null && menu !== false && !("broken" in menu) ? menu : null;
  const entries = useMemo(() => (shownMenu === null ? [] : entriesOf(shownMenu)), [shownMenu]);

  return (
    // `overflow-x-clip`: невидимая мишень 44 px у «?» на краю узкого окна не даёт странице горизонтальную прокрутку.
    <header ref={bar} className="sticky top-0 z-[var(--z-sticky)] flex min-h-header overflow-x-clip border-b border-line-subtle bg-chrome max-sm:flex-wrap max-sm:items-center max-sm:py-2xs">
      {/* Логотип ведёт на дашборд — после входа. До входа ведёт некуда. */}
      <div className="flex shrink-0 items-center px-md lg:w-sidebar lg:border-r lg:border-line-subtle">
        <Brand linked={signedIn ? home : undefined} />
      </div>
      {/* Контейнер запроса: ширина шапки справа от меню равна ширине `main`, и заголовку
          считается тот же левый край, что у полотна: поле `xl` + половина свободного места
          вокруг колонки `max-w-page` (поле справа сидит на самих кнопках: оно не должно съедать ширину контейнера). */}
      <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-x-md gap-y-2xs py-2xs [container-type:inline-size] max-sm:contents">
        {/* Узкое окно (телефон): две строки — сверху поиск и профиль, ниже меню и заголовок целиком, не «К…». */}
        <div className="flex max-w-full min-w-0 shrink-0 items-center gap-sm pl-md max-sm:order-last max-sm:basis-full lg:shrink lg:pl-[calc(var(--space-xl)+max(0px,(100cqw-2*var(--space-xl)-var(--page-width))/2))]">
          {signedIn ? (
            <button
              type="button"
              onClick={onMenu}
              aria-expanded={menuOpen}
              aria-controls={menuId}
              className="relative inline-flex items-center gap-1.5 rounded-control border border-line px-xs py-2xs text-sm text-ink-2 transition-colors after:absolute after:-inset-touch-pad after:content-[''] hover:text-ink lg:hidden"
            >
              {menuOpen ? <X aria-hidden="true" className="size-4" /> : <MenuIcon aria-hidden="true" className="size-4" />}
              <span className="sr-only sm:not-sr-only">{menuOpen ? uk.nav.close : uk.nav.open}</span>
            </button>
          ) : null}
          {/* Место заголовка экрана (`@/components/ui/page-header`). */}
          <div ref={onSlot} className="flex min-w-0 items-center" />
        </div>
        <div className="ml-auto flex items-center gap-sm pr-md">
          {shownMenu === null ? null : <MenuSearch entries={entries} onGo={onGo} />}
          {signedIn ? <Profile me={me} /> : null}
        </div>
      </div>
    </header>
  );
}

function Frame() {
  useWordsVersion();
  const shell = useShell();
  const signedIn = shell !== null && shell.me !== null && shell.me !== false;
  const menuId = useId();
  const [menuOpen, setMenuOpen] = useState(false);
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const main = useRef<HTMLElement>(null);
  const at = useRouterState({ select: (one) => one.location.pathname });
  const [arrived, setArrived] = useState("");
  const first = useRef(true);
  // Смена страницы — вслух названием: заголовок стоит в шапке, фокус туда не
  // переносится (он остаётся у меню, откуда человек шёл), а без объявления
  // чтец молчал о том, что страница сменилась. Название — из заголовка вкладки,
  // который ставит сама страница.
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    // Перешли из меню — фокус в начало содержимого, а не на следующий пункт
    // меню: иначе клавиатура каждый раз проходит меню заново.
    if (document.activeElement?.closest("nav") != null) main.current?.focus({ preventScroll: true });
    const timer = window.setTimeout(() => setArrived(document.title.split(" · ")[0] ?? ""), 150);
    return () => window.clearTimeout(timer);
  }, [at]);

  return (
    <TitleSlot.Provider value={slot}>
      <p role="status" aria-live="polite" className="sr-only">
        {arrived}
      </p>
      {/* Живая область меню — в оболочке, а не в `<nav>`: закрытое меню узкого окна
          скрыто целиком, и область внутри него молчала бы. Стоит всегда, до первой плашки. */}
      <LiveRegion />
      <div className="flex min-h-screen flex-col bg-canvas text-ink antialiased">
        {/* «До змісту» — первая в порядке фокуса; видна, только когда на ней фокус. */}
        <a
          href="#main"
          onClick={(event) => {
            event.preventDefault();
            main.current?.focus();
          }}
          className="sr-only z-[var(--z-popover)] rounded-control bg-raised px-sm py-xs text-sm text-ink focus:not-sr-only focus:fixed focus:top-2xs focus:left-2xs"
        >
          {uk.shell.skip}
        </a>
        <Header
          menuId={menuId}
          menuOpen={menuOpen}
          onMenu={() => setMenuOpen((was) => !was)}
          onGo={() => setMenuOpen(false)}
          onSlot={setSlot}
        />
        <div className="flex flex-1 flex-col lg:flex-row">
          {/* Подложка меню тянется на всю высоту страницы: липкое меню короче
              длинной страницы, и без подложки под ним открывалось полотно. */}
          {/* Колонка меню занята и пока «кто вошёл» не ответило (`me === null`): иначе полотно сперва растёт
              на всю ширину и сдвигается на 15,5rem, когда ответ пришёл, а заголовок в шапке уже стоит по узкому полотну. */}
          {shell?.me !== false ? (
            <div data-menu-column className="shrink-0 border-line-subtle bg-chrome lg:w-sidebar lg:border-r">
              {signedIn ? (
                <Nav id={menuId} open={menuOpen} onGo={() => setMenuOpen(false)} menu={shell?.menu ?? null} shell={shell} />
              ) : null}
            </div>
          ) : null}
          <main
            id="main"
            ref={main}
            tabIndex={-1}
            className="min-w-0 flex-1 px-md py-lg lg:px-xl"
          >
            <div className="mx-auto max-w-page">
              <Outlet />
            </div>
          </main>
        </div>
        <StatusBar shell={shell} />
      </div>
    </TitleSlot.Provider>
  );
}

export function Shell() {
  // ⚠ Подписка на словарь: вход и выход меняют словарь, и каркас обязан
  // перерисоваться новыми словами, а не остаться с кодами.
  useWordsVersion();
  return (
    <ShellProvider>
      <Rewatch />
      <SessionWatch />
      <MustChangeWatch />
      <Frame />
    </ShellProvider>
  );
}

/** Состояние каркаса, пока ни одной поверхности не подключено. */
export function NoSurfaces() {
  return (
    <section>
      <h1 className="text-xl font-semibold">{uk.shell.noSurfaces}</h1>
      <p className="mt-2 text-ink-2">{uk.shell.noSurfacesHint}</p>
    </section>
  );
}

/** Адрес, которого нет ни у одной поверхности. */
export function NotFound() {
  return (
    <section>
      <h1 className="text-xl font-semibold">{uk.shell.notFound}</h1>
      <p className="mt-2 text-ink-2">{uk.shell.notFoundHint}</p>
    </section>
  );
}
